import { useCallback, useContext, useState } from "react";
import {
  defaultTwitchConfig,
  type TwitchConfig,
  TwitchConfigContext,
} from "@/context/twitchConfigContext";
import { type ChangeEventLike, restoreFromLocalStorage } from "@/util/localStorageConfig.ts";

// Re-exported for callers that used to import these from the hook module.
export { defaultTwitchConfig, type TwitchConfig };

/** Hook that reads from the shared TwitchConfigContext. */
export default function useTwitchConfig() {
  return useContext(TwitchConfigContext);
}

/** Returns the state and setters needed to back TwitchConfigContext — used by TwitchConfigProvider. */
export function useTwitchConfigState() {
  const [config, setConfig] = useState<TwitchConfig>(() =>
    restoreFromLocalStorage(defaultTwitchConfig),
  );

  const setField = useCallback(<K extends keyof TwitchConfig>(key: K, value: TwitchConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    localStorage.setItem(key, String(value));
  }, []);

  const setFromEvent = useCallback((e: ChangeEventLike) => {
    const { name, value } = e.target;
    setConfig((prev) => ({ ...prev, [name]: value }));
    localStorage.setItem(name, value);
  }, []);

  const clearField = useCallback((key: keyof TwitchConfig) => {
    setConfig((prev) => ({ ...prev, [key]: defaultTwitchConfig[key] }));
    localStorage.removeItem(key);
  }, []);

  return { config, setField, setFromEvent, clearField };
}
